import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';

import { closeSnackbar, enqueueSnackbar } from 'notistack';

import type { SnackbarKey } from 'notistack';

export interface SnackbarMessage {
  loading: string;
  success: string;
  error: string;
}

interface SnackbarNotificationsContextValue {
  showLoadingSnackbar: (requestKey: string, message: string) => SnackbarKey;
  showSuccessSnackbar: (requestKey: string, message: string) => SnackbarKey;
  showErrorSnackbar: (
    requestKey: string,
    message: string,
    autoHideDuration?: number
  ) => SnackbarKey;
  dismissNotification: (requestKey: string) => void;
}

const SnackbarNotificationsContext = createContext<SnackbarNotificationsContextValue | undefined>(
  undefined
);

interface SnackbarNotificationsProviderProps {
  children: ReactNode;
}

export const SnackbarNotificationsProvider = ({ children }: SnackbarNotificationsProviderProps) => {
  const notificationsMapRef = useRef<Map<string, SnackbarKey>>(new Map());

  const showLoadingSnackbar = useCallback((requestKey: string, message: string): SnackbarKey => {
    const existingKey = notificationsMapRef.current.get(requestKey);
    if (existingKey !== undefined) {
      closeSnackbar(existingKey);
    }

    const snackbarKey = enqueueSnackbar(message, {
      variant: 'loading',
      persist: true
    });

    notificationsMapRef.current.set(requestKey, snackbarKey);
    return snackbarKey;
  }, []);

  const showSuccessSnackbar = useCallback((requestKey: string, message: string): SnackbarKey => {
    const existingKey = notificationsMapRef.current.get(requestKey);
    if (existingKey !== undefined) {
      closeSnackbar(existingKey);
    }

    const snackbarKey = enqueueSnackbar(message, {
      variant: 'success',
      persist: false,
      autoHideDuration: 5000
    });

    notificationsMapRef.current.set(requestKey, snackbarKey);

    setTimeout(() => {
      if (notificationsMapRef.current.get(requestKey) === snackbarKey) {
        notificationsMapRef.current.delete(requestKey);
      }
    }, 5000);

    return snackbarKey;
  }, []);

  const showErrorSnackbar = useCallback(
    (requestKey: string, message: string, autoHideDuration?: number): SnackbarKey => {
      const existingKey = notificationsMapRef.current.get(requestKey);
      if (existingKey !== undefined) {
        closeSnackbar(existingKey);
      }

      const snackbarKey = enqueueSnackbar(message, {
        variant: 'error',
        persist: !autoHideDuration,
        autoHideDuration
      });

      notificationsMapRef.current.set(requestKey, snackbarKey);

      if (autoHideDuration) {
        setTimeout(() => {
          if (notificationsMapRef.current.get(requestKey) === snackbarKey) {
            notificationsMapRef.current.delete(requestKey);
          }
        }, autoHideDuration);
      }

      return snackbarKey;
    },
    []
  );

  const dismissNotification = useCallback((requestKey: string) => {
    const existingKey = notificationsMapRef.current.get(requestKey);
    if (existingKey !== undefined) {
      closeSnackbar(existingKey);
      notificationsMapRef.current.delete(requestKey);
    }
  }, []);

  const value: SnackbarNotificationsContextValue = useMemo(
    () => ({
      showLoadingSnackbar,
      showSuccessSnackbar,
      showErrorSnackbar,
      dismissNotification
    }),
    [dismissNotification, showErrorSnackbar, showLoadingSnackbar, showSuccessSnackbar]
  );

  return (
    <SnackbarNotificationsContext.Provider value={value}>
      {children}
    </SnackbarNotificationsContext.Provider>
  );
};

export const useSnackbarNotifications = () => {
  const context = useContext(SnackbarNotificationsContext);
  if (context === undefined) {
    throw new Error('useSnackbarNotifications must be used within a SnackbarNotificationsProvider');
  }
  return context;
};
